import { useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { arrayToMap, groupBy, last, min, nonNullable, sortBy, unique } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { getAlwaysOnCancellationPolicyDays, isAlwaysOnCancellationPolicyRefundable } from 'lib/payment/alwaysOnUtils'
import {
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import { extractPkgInclusions } from 'lib/offer/inclusionUtils'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'

export interface LPCRateRankingView {
  /**
   * Unique key of the view this represents
   */
  uniqueKey: string;
  /**
   * The price of the view to rank against
   */
  price: number;
  /**
   * When the rate no longer provides a full refund
   * This should be today if it's non-refundable
   */
  fullRefundDeadline: moment.Moment;
  /**
   * The latest date a rate allows a partial refund
   * Not all policies support partial, so can be undefined
   */
  partialRefundDeadline?: moment.Moment;
  /**
   * The penalty (as a percentage) how much will be lost
   */
  partialPenalty: number;
  /**
   * A unique key that defines the set of inclusions
   */
  inclusionsKey: string;
}

export function getBestRankedOptionKeys(rankingViews: Array<LPCRateRankingView>): Array<string> {
  const viewsByInclusionKey = groupBy(rankingViews, view => view.inclusionsKey)
  // now find the "best" options within each inclusion group
  return viewsByInclusionKey.values().flatMap((views) => {
    // We will find the following:
    // - Group by fully refundable deadline
    //   - Filter to the cheapest options
    //   - Group cheapest options by their Partial refund length
    //   - Take the option with lowest partial penalty (1)
    // - Get the ranks for the above groups
    // - Filter every option that has a has a cheaper but high ranked item than it

    const groupedByRefundableDeadline = groupBy(views, view => view.fullRefundDeadline.format(ISO_DATE_FORMAT))
    const cheapestRefundableGroupViews = groupedByRefundableDeadline.values().map(group => {
      // find all those with the same price
      const refundableGroupByPrice = groupBy(group, view => view.price)
      // now get the cheapest priced options. groupBy never creates empty groups, so cheapestGroupEntry must exist
      const cheapestGroupEntry = min(refundableGroupByPrice.entries().toArray(), ([price]) => price)!
      // if there's multiple, find the one with the best (lowest) penalty
      return min(cheapestGroupEntry[1], view => view.partialPenalty)!
    }).toArray()

    const worstToBestDeadlineOptions = sortBy(cheapestRefundableGroupViews, view => view.fullRefundDeadline.unix(), 'asc')
    return worstToBestDeadlineOptions.filter((option, index) => {
      const nextOption = worstToBestDeadlineOptions[index + 1]
      if (!nextOption) {
        // No next means we're at the best ranked option
        // this is always included
        return true
      }

      // we've already made sure this is a worse ranked deadline
      // so only include us when our price is better than someone ranked above us
      return option.price < nextOption.price
    }).map(option => option.uniqueKey)
  }).toArray()
}

export function getLPCUniqueOptions(
  options: Array<OfferPackageOptionView>,
  offerAvailableRates: Array<App.OfferAvailableRate>,
  checkIn: string | undefined,
  checkOut: string | undefined,
  previewMode: boolean,
  roomRateId: string | undefined,
  inclusionsViewableLuxPlusTier: App.MembershipSubscriptionTier,
  useDynamicCancellationPolicies: boolean,
): Array<OfferPackageOptionView> {
  if (previewMode) {
    return options
  }

  const highlightedOption = roomRateId ?
    options.find((option) => option.package.roomRate?.id === roomRateId) :
    undefined

  const ratesByPackageKey = arrayToMap(offerAvailableRates, rate => rate.packageUniqueKey)
  const rankingViews = options.map((option): LPCRateRankingView => {
    const price = option.hotelMemberTotal || option.hotelTotal

    const inclusions = extractPkgInclusions(option.package, { checkIn, checkOut, luxPlusTier: inclusionsViewableLuxPlusTier })
    const inclusionsKey = unique(inclusions.flatMap(group => group.map(incl => incl.description))).sort().join('-')

    if (useDynamicCancellationPolicies) {
      const policies = ratesByPackageKey.get(option.package.uniqueKey)?.cancellationPolicies?.policies ?? []
      // We assume the policies are in deadline order already
      const firstPolicy = policies[0]
      const lastPartialPolicy = policies.length > 2 ? last(policies.filter(policy => policy.amountPercent?.amount !== policy.amountPercent?.sell)) : undefined
      return {
        uniqueKey: option.package.uniqueKey,
        inclusionsKey,
        price,
        fullRefundDeadline: firstPolicy ? moment(firstPolicy.deadline) : moment(),
        partialRefundDeadline: lastPartialPolicy ? moment(lastPartialPolicy.deadline) : undefined,
        partialPenalty: lastPartialPolicy?.amountPercent ? (lastPartialPolicy.amountPercent.amount / lastPartialPolicy.amountPercent?.sell) * 100 : 100,
      }
    } else if (option.package.roomRate?.cancellationPolicy) {
      const refundable = isAlwaysOnCancellationPolicyRefundable(option.package.roomRate.cancellationPolicy.type)
      const deadline = moment(checkIn).subtract(getAlwaysOnCancellationPolicyDays(option.package.roomRate?.cancellationPolicy.type), 'days')
      return {
        uniqueKey: option.package.uniqueKey,
        inclusionsKey,
        price,
        fullRefundDeadline: refundable ? deadline : moment(),
        // this style of cancellation policy doesn't do partial cancellation
        partialRefundDeadline: undefined,
        partialPenalty: 100,
      }
    }
    // assume non-refundable if we have no policy information
    return {
      uniqueKey: option.package.uniqueKey,
      inclusionsKey,
      price,
      fullRefundDeadline: moment(),
      partialRefundDeadline: undefined,
      partialPenalty: 100,
    }
  })
  const optionsByUniqueKey = arrayToMap(options, option => option.package.uniqueKey)
  const packageKeys = getBestRankedOptionKeys(rankingViews)
  const uniqueOptions = nonNullable(packageKeys.map(key => optionsByUniqueKey.get(key)))
  if (!highlightedOption) return uniqueOptions
  return [highlightedOption, ...uniqueOptions.filter((option) => option !== highlightedOption)]
}

/** @deprecated Do not use this hook for SPP offers. */
function useLPCUniqueOptions(
  offerId: string,
  options: Array<OfferPackageOptionView>,
  params: {
    useDynamicCancellationPolicies?: boolean;
    duration?: number;
    checkIn?: string;
    rooms?: Array<App.Occupants>;
    roomRateId?: string;
  } = EmptyObject,
): Array<OfferPackageOptionView> {
  const {
    useDynamicCancellationPolicies = false,
    duration,
    checkIn,
    rooms = initialRooms,
    roomRateId,
  } = params
  const previewMode = useAppSelector(state => state.system.previewMode)
  const inclusionsViewableLuxPlusTier = useAppSelector(getInclusionsViewableLuxPlusTier)
  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, duration])
  const offerAvailableRates = useOfferAvailableRates(offerId, {
    checkIn,
    checkOut,
    rooms,
  })

  return useMemo(() => getLPCUniqueOptions(
    options,
    offerAvailableRates.rates,
    checkIn,
    checkOut,
    previewMode,
    roomRateId,
    inclusionsViewableLuxPlusTier,
    useDynamicCancellationPolicies,
  ), [
    checkIn,
    checkOut,
    inclusionsViewableLuxPlusTier,
    offerAvailableRates.rates,
    options,
    previewMode,
    roomRateId,
    useDynamicCancellationPolicies,
  ])
}

export default useLPCUniqueOptions
